import { Link, useLocation } from "wouter";
import { Compass } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SnowField } from "@/components/SnowField";
import { AmbientBells } from "@/components/AmbientBells";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useDocumentMeta } from "@/lib/seo";
import {
  AuthSceneKeyframes,
  BokehOrbs,
  GoldGlints,
  GoldParticles,
  JMonogram,
  StorybookBackdrop,
  VillageSilhouettes,
  usePrefersReducedMotion,
} from "@/components/AuthScene";

// Phase 4 polish: bring the 404 surface up to the same gold-edged storybook
// look that sign-in / reset / dashboards already wear, so a mistyped URL
// doesn't snap a player out of the world.
export default function NotFound() {
  const [, setLocation] = useLocation();
  const reduced = usePrefersReducedMotion();
  const { t } = useTranslation();
  // Static SPA hosting can't return a true 404 status, but we can at
  // least keep this URL out of the index by emitting noindex per-route.
  useDocumentMeta({
    title: "This clue wandered off · JINGLE",
    description:
      "The page you're looking for couldn't be found. Head back to JINGLE and keep the Christmas mission moving.",
    canonicalPath: "/",
    robots: "noindex, follow",
  });

  return (
    <div
      className="dark relative min-h-screen overflow-hidden bg-[hsl(354,60%,7%)] text-foreground"
      data-testid="not-found-page"
    >
      <StorybookBackdrop reduced={reduced} />
      <VillageSilhouettes />
      {!reduced ? <BokehOrbs count={6} /> : null}
      {!reduced ? <SnowField flakeCount={90} /> : null}
      {!reduced ? <GoldParticles count={36} /> : null}
      {!reduced ? <GoldGlints count={4} /> : null}
      <AmbientBells />

      {/* Top chrome — back button + language picker, mirroring sign-in. */}
      <div className="absolute inset-x-0 top-0 z-20 flex items-start justify-between px-5 pt-5 sm:px-8 sm:pt-7">
        <button
          type="button"
          onClick={() => setLocation("/")}
          className="rounded-md text-xs uppercase tracking-[0.4em] text-amber-100/70 transition-colors hover:text-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(354,60%,10%)]"
          data-testid="button-back-splash"
        >
          ← {t("notFound.back")}
        </button>
        <LanguageSelector />
      </div>

      {/* Main content */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center px-5 pb-16 pt-24 sm:px-8 sm:pt-28">
        <JMonogram />
        <p
          className="mt-3 flex items-center gap-3 text-[11px] uppercase tracking-[0.55em]"
          style={{ color: "hsl(40, 90%, 75%)" }}
        >
          <span aria-hidden className="text-amber-200/70">✦</span>
          {t("notFound.kicker")}
          <span aria-hidden className="text-amber-200/70">✦</span>
        </p>

        {/* 404 numerals with the same warm glow halo as the auth headlines. */}
        <div className="relative mt-3">
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[180%] w-[140%] -translate-x-1/2 -translate-y-1/2"
            style={{
              background:
                "radial-gradient(60% 50% at 50% 50%, hsla(40, 95%, 60%, 0.32) 0%, hsla(0, 80%, 55%, 0.10) 45%, transparent 75%)",
              filter: "blur(8px)",
              animation: reduced
                ? undefined
                : "authHeadlineGlow 7s ease-in-out infinite",
            }}
          />
          <h1
            className="relative text-center font-serif text-[7rem] font-medium leading-[0.95] tracking-tight sm:text-[9rem]"
            style={{
              color: "hsl(40, 70%, 92%)",
              textShadow:
                "0 4px 30px hsla(40, 95%, 60%, 0.45), 0 2px 10px hsla(0, 80%, 55%, 0.45), 0 0 60px hsla(40, 95%, 65%, 0.18)",
            }}
          >
            404
          </h1>
        </div>
        <div
          aria-hidden
          className="mt-4 flex items-center gap-2 text-amber-200/70"
        >
          <span className="h-px w-10 bg-gradient-to-r from-transparent to-amber-200/60" />
          <span className="text-xs">✦</span>
          <span className="h-px w-10 bg-gradient-to-l from-transparent to-amber-200/60" />
        </div>

        {/* Storybook card — same pine glass primitive as the dashboards. */}
        <div className="jingle-card-pine relative mt-8 w-full max-w-[520px] overflow-hidden rounded-[28px] p-6 sm:p-8">
          {/* refined top highlight */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-6 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, hsla(40, 95%, 85%, 0.55) 50%, transparent 100%)",
            }}
          />
          {/* inner warm glow — gives the card a "lit from within" feel */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[28px]"
            style={{
              background:
                "radial-gradient(80% 60% at 50% 35%, hsla(0, 90%, 55%, 0.18) 0%, transparent 65%)",
            }}
          />

          <div className="relative flex flex-col items-center text-center">
            <span
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-amber-200/45 bg-[hsl(354,60%,12%)]/85"
              style={{
                boxShadow:
                  "0 0 0 1px hsla(40, 95%, 70%, 0.20), 0 0 22px hsla(40, 90%, 60%, 0.45), inset 0 1px 0 hsla(40, 95%, 85%, 0.25)",
              }}
              aria-hidden
            >
              <Compass className="h-4 w-4 text-amber-200/95" />
            </span>
            <p
              className="mt-3 flex items-center gap-2 text-xs italic"
              style={{ color: "hsl(40, 80%, 78%)" }}
            >
              <span aria-hidden className="text-[10px]">✦</span>
              {t("notFound.eyebrow")}
              <span aria-hidden className="text-[10px]">✦</span>
            </p>

            <p className="mt-5 max-w-sm text-base leading-relaxed text-amber-100/85">
              {t("notFound.body")}
            </p>

            <Link
              href="/"
              data-testid="link-home"
              className="group/btn relative mt-8 inline-flex h-14 w-full items-center justify-center overflow-hidden rounded-2xl border border-amber-200/35 text-base font-semibold tracking-wide text-amber-50 transition-all hover:border-amber-200/60 focus-visible:ring-2 focus-visible:ring-amber-200/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(354,55%,12%)]"
              style={{
                background:
                  "linear-gradient(180deg, hsl(0, 85%, 55%) 0%, hsl(354, 80%, 40%) 55%, hsl(354, 78%, 30%) 100%)",
                boxShadow:
                  "0 0 0 1px hsla(40, 95%, 70%, 0.30), 0 16px 34px -10px hsla(0, 85%, 40%, 0.70), 0 0 30px hsla(0, 95%, 55%, 0.55), 0 0 60px -10px hsla(40, 95%, 60%, 0.35), inset 0 1px 0 hsla(40, 95%, 85%, 0.45), inset 0 -10px 26px hsla(0, 90%, 22%, 0.55)",
              }}
            >
              {/* warm inner top highlight */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-3 top-0 h-px"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, hsla(40, 95%, 90%, 0.7), transparent)",
                }}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-amber-200/85"
                style={{ textShadow: "0 0 10px hsla(40, 95%, 65%, 0.7)" }}
              >
                ✦
              </span>
              <span
                aria-hidden
                className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-amber-200/85"
                style={{ textShadow: "0 0 10px hsla(40, 95%, 65%, 0.7)" }}
              >
                ✦
              </span>
              <span className="relative">{t("notFound.cta")}</span>
            </Link>
          </div>
        </div>
      </div>

      <AuthSceneKeyframes />
    </div>
  );
}
